import { useContext, useState } from "react";
import { LanguageContext } from "../../../globals/LanguageContext";
import { StyledContactCard } from "./contactCard.styled";
import { Box, TextField, Typography } from "@mui/material";
import { Contact } from "../../../models/Contact";
import { DownloadSimple, DownloadSimpleIcon, PencilSimple, PencilSimpleIcon } from "@phosphor-icons/react";
import { Address } from "../../../models/Address";
import { useProfile } from "../../../stores/useProfile";
import { Project } from "../../../models/Project";
import { useProject } from "../../../stores/useProject";
import { formatPhoneNumber, formatVAT } from "../../../globals/utils";

type ContactCardProps = {
  project: Project;
};

const defaultContact = {
  company: "",
  firstName: "",
  lastName: "",
  address: {
    street: "",
    number: "",
    zipCode: "",
    city: "",
    country: "België",
  },
  email: "",
  phone: "",
  vat: "",
  peppolId: ""
};

export const ContactCard = ({ project }: ContactCardProps) => {
  const defaultValues = project.contact ? project.contact : defaultContact;
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({ ...defaultValues });
  const { dictionary } = useContext(LanguageContext);
  const { user } = useProfile();
  const { addContactToProject } = useProject();

  const handleInputChange = <K extends keyof Contact>(
    field: K,
    value: Contact[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAddressChange = <K extends keyof Address>(
    field: K,
    value: Address[K]
  ) => {
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }));
  };

  const handleChanges = async () => {
    if (!user) return;

    try {
      if (project.id) {
        console.log(formData)
        await addContactToProject(user, project.id, formData);
      }

      setEditMode(false);
    } catch (error) {
      console.error("Failed to save contact and assign to project", error);
    }
  };

  return (
    <StyledContactCard>
      <Box className="head">
        <Typography variant="h3">
          {dictionary.pages.projects.project.customer.title}
        </Typography>
        {editMode ? (
          <DownloadSimpleIcon
            size={32}
            className="button"
            weight="bold"
            onClick={handleChanges}
            style={{ cursor: "pointer" }}
          />
        ) : (
          <PencilSimpleIcon
            size={32}
            className="button"
            weight="bold"
            onClick={() => setEditMode(true)}
            style={{ cursor: "pointer" }}
          />
        )}
      </Box>

      <Box className="content">
        <Box className="row">
          {editMode ? (
            <TextField
              label="Company"
              value={formData.company}
              fullWidth
              onChange={(e) => handleInputChange("company", e.target.value)}
            />
          ) : (
            <Typography variant="h4">{formData.company}</Typography>
          )}
        </Box>

        <Box className="row">
          {editMode ? (
            <>
              <TextField
                label="Last Name"
                value={formData.lastName}
                fullWidth
                onChange={(e) => handleInputChange("lastName", e.target.value)}
              />
              <TextField
                label="First Name"
                value={formData.firstName}
                fullWidth
                onChange={(e) => handleInputChange("firstName", e.target.value)}
              />
            </>
          ) : (
            <Typography variant="h4">
              {formData.lastName} {formData.firstName}
            </Typography>
          )}
        </Box>

        <Box className="row">
          {editMode ? (
            <>
              <TextField
                label="Street"
                value={formData.address.street}
                fullWidth
                onChange={(e) => handleAddressChange("street", e.target.value)}
              />
              <TextField
                label="Number"
                fullWidth
                value={formData.address.number}
                onChange={(e) => handleAddressChange("number", e.target.value)}
              />
            </>
          ) : (
            <>
              <Typography variant="h4">{formData.address.street}</Typography>
              <Typography variant="h4">{formData.address.number}</Typography>
            </>
          )}
        </Box>

        <Box className="row">
          {editMode ? (
            <>
              <TextField
                label="ZIP Code"
                fullWidth
                value={formData.address.zipCode}
                onChange={(e) => handleAddressChange("zipCode", e.target.value)}
              />
              <TextField
                label="City"
                fullWidth
                value={formData.address.city}
                onChange={(e) => handleAddressChange("city", e.target.value)}
              />
            </>
          ) : (
            <>
              <Typography variant="h4">{formData.address.zipCode}</Typography>
              <Typography variant="h4">{formData.address.city}</Typography>
            </>
          )}
        </Box>

        <Box className="row">
          {editMode ? (
            <TextField
              label="Country"
              fullWidth
              value={formData.address.country}
              onChange={(e) => handleAddressChange("country", e.target.value)}
            />
          ) : (
            <Typography variant="h4">{formData.address.country}</Typography>
          )}
        </Box>

        <Box className="row">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.customer.email}:
          </Typography>
          {editMode ? (
            <TextField
              fullWidth
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
            />
          ) : (
            <Typography variant="h4" className="end">{formData.email}</Typography>
          )}
        </Box>

        <Box className="row">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.customer.phone}:
          </Typography>
          {editMode ? (
            <TextField
              fullWidth
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              onBlur={(e) =>
                handleInputChange("phone", formatPhoneNumber(e.target.value))
              }
            />
          ) : (
            <Typography variant="h4" className="end">{formData.phone}</Typography>
          )}
        </Box>

        <Box className="row">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.customer.vat}:
          </Typography>
          {editMode ? (
            <TextField
              fullWidth
              value={formData.vat}
              onChange={(e) => handleInputChange("vat", e.target.value)}
              onBlur={(e) =>
                handleInputChange("vat", formatVAT(e.target.value))
              }
            />
          ) : (
            <Typography variant="h4" className="end">{formData.vat}</Typography>
          )}
        </Box>
        <Box className="row">
          <Typography variant="h4" className="contactInfo">
            {dictionary.pages.projects.project.customer.peppolId}:
          </Typography>
          {editMode ? (
            <TextField
              fullWidth
              value={formData.peppolId}
              onChange={(e) => handleInputChange("peppolId", e.target.value)}
              onBlur={(e) =>
                handleInputChange("peppolId", e.target.value)
              }
            />
          ) : (
            <Typography variant="h4" className="end">{formData.peppolId}</Typography>
          )}
        </Box>
      </Box>
    </StyledContactCard>
  );
};
